import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Datagrid,
  TextField,
  FunctionField,
  Filter,
  SelectInput,
  TextInput,
  Link,
  Identifier,
  useTranslate,
  Record,
  useListContext,
  SimpleList,
  Responsive,
} from "react-admin";
import TrailerOutlineIcon from "../../../common/icons/TrailerOutlineIcon";
import Timer from "@material-ui/icons/Timer";
import AccessTime from "@material-ui/icons/AccessTime";
import Loop from "@material-ui/icons/Loop";
import TruckOutlineIcon from "../../../common/icons/TruckOutlineIcon";

import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import dataGridFields from "../../common/data-grid-fields";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import LocationOffIcon from "@material-ui/icons/LocationOff";
import {
  ForTrue,
  ForFalse,
} from "../../common/data-grid-fields/ConditionalField";
import { Tooltip, Theme, useTheme } from "@material-ui/core";
import "./drivers-view-list.css";
import { Void } from "../../common/data-grid-fields/Void";
import "../../common/styles/list-style.css";
import resourceNames from "../../../common/data/resourceNames";
import { AppbarGroupsFilter } from "../../common/data-grid-inputs/AppBarGroupsFilter";
import { DriverEldDisconnectedField } from "./DriverEldDisconnectedField";
import { DriverNoViolationsField } from "./DriverNoViolationsField";
import { DriverWithViolationsField } from "./DriverWithViolationsField";
import {
  HosDriverDto,
  RecordStatus,
} from "../../../common/data/service-proxies";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import drivers from ".";
import { DriverEldConnectedField } from "./DriverEldConnectedField";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { AppbarCompaniesFilter } from "../../common/data-grid-inputs/AppBarCompaniesFilter";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import {
  HosInfoContext,
  HosInfoSetterContext,
  IHosInfoContext,
} from "./HosInfoContext";
import { getHosTimers, HosTimers } from "../hosUtils";
import { HosInfoField } from "./HosInfoField";

interface FilterProps {
  selectedGroup?: Identifier;
  selectedCompanies?: Array<Identifier>;
}

const dutyStatuses = [
  { name: "appData.dutyCycles.OFF", id: "OFF" },
  { name: "appData.dutyCycles.SB", id: "SB" },
  { name: "appData.dutyCycles.D", id: "D" },
  { name: "appData.dutyCycles.ON", id: "ON" },
];

const driverStatuses = [
  { id: null, name: "All" },
  { id: RecordStatus.Active, name: "Active" },
  { id: RecordStatus.Inactive, name: "Inactive" },
];

const DriversListFilter = (props: any) => {
  useSearchFields(props, [
    "driverDisplayName",
    "vehicleNumber",
    "trailerNumber",
  ]);

  const { setHosInfos } = useContext(HosInfoSetterContext);
  useResetListSearchField(props);
  const { loading, data, ids } = useListContext();
  useEffect(() => {
    if (!data || !ids || !ids.length || !setHosInfos) {
      return;
    }

    const result = new Map<Identifier, HosTimers>(
      ids.map((x) => [x, getHosTimers(data[x] as HosDriverDto)])
    );

    setHosInfos({ data: result });
  }, [data, ids, setHosInfos]);

  return (
    <Filter {...props}>
      {!loading ? (
        <TextInput
          className="search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      ) : (
        <TextInput
          className="search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
      <SelectInput
        className="filter-box"
        label="resources.drivers.labels.filterByDutyStatus"
        allowEmpty
        emptyText="resources.common.labels.all"
        source="driverDutyStatus"
        alwaysOn
        choices={dutyStatuses}
      />
      <SelectInput
        className="filter-box"
        label="Driver status"
        source="driverStatus"
        choices={driverStatuses}
        allowEmpty={false}
        alwaysOn
      />
    </Filter>
  );
};

export const DriversListView = (props: any) => {
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const [selectedGroup, setSelectedGroup] = useState<Identifier>("");
  const permissions = useActionPermissions();
  const [selectedCompanies, setSelectedCompanies] = useState<Array<string>>([]);
  const handleSelectedCompaniesChange = useCallback((e: string[]) => {
    setSelectedCompanies(e);
  }, []);
  const [hosInfos, setHosInfos] = useState<IHosInfoContext>({
    data: new Map<Identifier, HosTimers>(),
  });

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}>
        {record ? record.id + " | " + record.driverDisplayName : null}
        <FunctionField<any>
          label="resources.drivers.labels.dutyStatus"
          source="driverDutyStatus"
          render={() => {
            return record ? (
              permissions[permissionNames.hos.logs.view] ? (
                <Link
                  to={`/${resourceNames.HOS_DRIVER_LOG_VIEW}/${
                    record.id
                  }/${new Date().toISOString()}`}>
                  <dataGridFields.ColoredStatusField
                    record={record}
                    color={getColor}
                    backgroundColor={getBackgroundColor}
                    applyColorTo="everything"
                    labelStyle="columns"
                    source="driverDutyStatus"
                    labelTranslationNamespace="appData.dutyCycles"
                    secondLabelSource="driverDutyStatusDuration"
                    secondLabelRenderComponent={(props: any) => (
                      <dataGridFields.TimeField
                        {...props}
                        inputType="minutes"
                        outputType="short"
                      />
                    )}
                    tooltip="resources.drivers.labels.openCurrentDriverLog"
                  />
                </Link>
              ) : (
                <dataGridFields.ColoredStatusField
                  record={record}
                  color={getColor}
                  backgroundColor={getBackgroundColor}
                  applyColorTo="everything"
                  labelStyle="columns"
                  source="driverDutyStatus"
                  labelTranslationNamespace="appData.dutyCycles"
                  secondLabelSource="driverDutyStatusDuration"
                  secondLabelRenderComponent={(props: any) => (
                    <dataGridFields.TimeField
                      {...props}
                      inputType="minutes"
                      outputType="short"
                    />
                  )}
                  tooltip="resources.drivers.labels.openCurrentDriverLog"
                />
              )
            ) : (
              <dataGridFields.ColoredStatusField
                color={getColor}
                backgroundColor={getBackgroundColor}
                applyColorTo="everything"
                labelStyle="columns"
                source="driverDutyStatus"
                secondLabelSource="driverDutyStatusDuration"
                secondLabelRenderComponent={(props: any) => (
                  <dataGridFields.TimeField
                    {...props}
                    inputType="minutes"
                    outputType="short"
                  />
                )}
              />
            );
          }}
        />
      </div>
    );
  };

  const renderSecondaryTextForSimpleList = (record: Record) => {
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}>
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              marginTop: "14px",
              flexWrap: "wrap",
            }}>
            <Tooltip title="Vehicle number">
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <LocalShippingOutlinedIcon
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <TextField
                  style={{ color: theme.palette.text.secondary }}
                  source="vehicleNumber"
                  label="resources.drivers.labels.vehicle"
                  emptyText={translate(
                    "resources.drivers.labels.none"
                  )}></TextField>
              </div>
            </Tooltip>
            <Tooltip title="Trailer number">
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <TrailerOutlineIcon
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <TextField
                  style={{ color: theme.palette.text.secondary }}
                  source="trailerNumber"
                  label="resources.drivers.labels.trailer"
                  emptyText={translate(
                    "resources.drivers.labels.none"
                  )}></TextField>
              </div>
            </Tooltip>
          </div>
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              marginTop: "14px",
              flexWrap: "wrap",
            }}>
            <Tooltip title="Until break">
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.secondary,
                }}>
                <Timer
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <HosInfoField
                  record={record}
                  headerClassName="centered-content"
                  className="centered-content"
                  label="resources.drivers.labels.untilBreak"
                  field={(x) => x.break}
                />
              </div>
            </Tooltip>
            <Tooltip title="To drive">
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.secondary,
                }}>
                <TruckOutlineIcon
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <HosInfoField
                  headerClassName="centered-content"
                  className="centered-content"
                  record={record}
                  label="resources.drivers.labels.toDrive"
                  field={(x) => x.shiftDrive}
                />
              </div>
            </Tooltip>
            <Tooltip title="Shift time left">
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.secondary,
                }}>
                <AccessTime
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <HosInfoField
                  headerClassName="centered-content"
                  className="centered-content"
                  record={record}
                  label="resources.drivers.labels.shiftTimeLeft"
                  field={(x) => x.shiftWork}
                />
              </div>
            </Tooltip>
            <Tooltip title="Cycle time left">
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.text.secondary,
                }}>
                <Loop
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
                <HosInfoField
                  headerClassName="centered-content"
                  className="centered-content"
                  record={record}
                  label="resources.drivers.labels.cycleTimeLeft"
                  field={(x) => x.cycleWork}
                />
              </div>
            </Tooltip>
          </div>
        </div>

        <div
          style={{
            marginTop: "14px",
            display: "flex",
            flexDirection: "row",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            color: theme.palette.text.secondary,
          }}>
          <FunctionField<any>
            source="lastPositionName"
            render={renderLastPositionName}
            label="resources.drivers.labels.lastPosition"
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "6px",
              flexWrap: "wrap",
              alignItems: "center",
            }}>
            <FunctionField<Record>
              source="eldConnected"
              label="resources.drivers.labels.eldConnection"
              render={() => (
                <Tooltip title="ELD Connection">
                  <div className="centered-content">
                    {record?.eldConnected ? (
                      <DriverEldConnectedField />
                    ) : (
                      <DriverEldDisconnectedField />
                    )}
                  </div>
                </Tooltip>
              )}
            />
            <FunctionField<Record>
              source="violations"
              label="resources.drivers.labels.violations"
              render={() => (
                <div className="centered-content">
                  {record?.violations ? (
                    <DriverWithViolationsField history={props.history} />
                  ) : (
                    <DriverNoViolationsField />
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderLastPositionName = (record: any) => {
    const lastPositionName: string = record["lastPositionName"];
    const split = !!lastPositionName
      ? lastPositionName?.split(",")
      : [translate("resources.drivers.labels.unknown")];
    const splitSmall = !!lastPositionName
      ? lastPositionName
      : [translate("resources.drivers.labels.unknown")];
    const rightChildren = split.map((fragment, index) => (
      <div key={index}>{fragment}</div>
    ));
    return (
      <div
        className="last-position-container"
        onClick={() =>
          !!lastPositionName &&
          permissions[permissionNames.locations.current.main]
            ? props.history.push(
                `/${resourceNames.LOCATIONS_CURRENT}/${record.vehicleId}`
              )
            : {}
        }>
        <div style={{ color: theme.palette.info.main }}>
          {!!lastPositionName ? (
            <Tooltip
              title={translate(
                "resources.drivers.labels.openCurrentLocationView"
              )}>
              <LocationOnIcon />
            </Tooltip>
          ) : (
            <LocationOffIcon />
          )}
        </div>
        <div>
          <Responsive
            medium={<>{rightChildren}</>}
            small={<>{splitSmall}</>}></Responsive>
        </div>
      </div>
    );
  };

  const getColor = (
    record: any,
    source: string | undefined,
    darkTheme: boolean
  ) => {
    if (!record || !source) {
      return "auto";
    }

    if (!record[source]) {
      return "auto";
    }

    switch (record[source]) {
      case "OFF":
        return darkTheme
          ? theme.palette.error.contrastText
          : theme.palette.error.contrastText;
      case "ON":
        return darkTheme
          ? theme.palette.info.contrastText
          : theme.palette.info.contrastText;
      case "D":
        return darkTheme
          ? theme.palette.success.contrastText
          : theme.palette.success.contrastText;
      case "SB":
        return darkTheme
          ? theme.palette.warning.contrastText
          : theme.palette.warning.contrastText;
      default:
        return "auto";
    }
  };

  const getBackgroundColor = (
    record: any,
    source: string | undefined,
    darkTheme: boolean
  ) => {
    if (!record || !source) {
      return "auto";
    }

    if (!record[source]) {
      return "auto";
    }

    switch (record[source]) {
      case "OFF":
        return theme.palette.grey[900];
      case "ON":
        return darkTheme ? theme.palette.info.dark : theme.palette.info.light;
      case "D":
        return darkTheme
          ? theme.palette.success.dark
          : theme.palette.success.light;
      case "SB":
        return darkTheme
          ? theme.palette.warning.dark
          : theme.palette.warning.main;
      default:
        return "auto";
    }
  };

  const getFilter = ({ selectedGroup, selectedCompanies }: FilterProps) => {
    let retVal: any = {};
    if (selectedGroup) {
      retVal.groupId = selectedGroup;
    }

    if (selectedCompanies?.length) {
      retVal.companyIds = selectedCompanies.join(",");
    }

    return retVal;
  };

  return (
    <div className="content-container">
      <AppbarCompaniesFilter
        selectedCompanies={selectedCompanies}
        onSelectedCompaniesChange={handleSelectedCompaniesChange}
      />
      <AppbarGroupsFilter
        selectedGroup={selectedGroup}
        onSelectedGroupChange={(id) => setSelectedGroup(id)}
      />
      <HosInfoContext.Provider value={hosInfos}>
        <HosInfoSetterContext.Provider value={{ setHosInfos }}>
          <DefaultProLogsList
            className="content-list"
            title="resources.drivers.name"
            bulkActionButtons={false}
            actions={<Void />}
            filters={<DriversListFilter />}
            filter={getFilter({ selectedGroup, selectedCompanies })}
            empty={<ProLogsEmpty iconType={drivers.icon} />}
            sort={{ field: "driverDisplayName", order: "ASC" }}
            {...props}
          >
            <Responsive
              small={
                <SimpleList
                  className="simple-list-wrapper"
                  primaryText={(record) => (
                    <RenderPrimaryTextForSimpleList record={record} />
                  )}
                  secondaryText={renderSecondaryTextForSimpleList}
                  linkType={false}
                />
              }
              medium={
                <Datagrid>
                  <TextField
                    source="driverId"
                    label="resources.drivers.labels.driverId"
                  />
                  <TextField
                    source="driverDisplayName"
                    label="resources.drivers.labels.driver"
                  ></TextField>
                  <TextField
                    source="vehicleNumber"
                    label="resources.drivers.labels.vehicle"
                    emptyText={translate("resources.drivers.labels.none")}
                  ></TextField>
                  <TextField
                    source="trailerNumber"
                    label="resources.drivers.labels.trailer"
                    emptyText={translate("resources.drivers.labels.none")}
                  ></TextField>
                  <FunctionField<any>
                    label="resources.drivers.labels.dutyStatus"
                    source="driverDutyStatus"
                    render={(record) => {
                      return record ? (
                        permissions[permissionNames.hos.logs.view] ? (
                          <Link
                            to={`/${resourceNames.HOS_DRIVER_LOG_VIEW}/${
                              record.id
                            }/${new Date().toISOString()}`}
                          >
                            <dataGridFields.ColoredStatusField
                              record={record}
                              color={getColor}
                              backgroundColor={getBackgroundColor}
                              applyColorTo="everything"
                              labelStyle="columns"
                              source="driverDutyStatus"
                              labelTranslationNamespace="appData.dutyCycles"
                              secondLabelSource="driverDutyStatusDuration"
                              secondLabelRenderComponent={(props: any) => (
                                <dataGridFields.TimeField
                                  {...props}
                                  inputType="minutes"
                                  outputType="short"
                                />
                              )}
                              tooltip="resources.drivers.labels.openCurrentDriverLog"
                            />
                          </Link>
                        ) : (
                          <dataGridFields.ColoredStatusField
                            record={record}
                            color={getColor}
                            backgroundColor={getBackgroundColor}
                            applyColorTo="everything"
                            labelStyle="columns"
                            source="driverDutyStatus"
                            labelTranslationNamespace="appData.dutyCycles"
                            secondLabelSource="driverDutyStatusDuration"
                            secondLabelRenderComponent={(props: any) => (
                              <dataGridFields.TimeField
                                {...props}
                                inputType="minutes"
                                outputType="short"
                              />
                            )}
                            tooltip="resources.drivers.labels.openCurrentDriverLog"
                          />
                        )
                      ) : (
                        <dataGridFields.ColoredStatusField
                          color={getColor}
                          backgroundColor={getBackgroundColor}
                          applyColorTo="everything"
                          labelStyle="columns"
                          source="driverDutyStatus"
                          secondLabelSource="driverDutyStatusDuration"
                          secondLabelRenderComponent={(props: any) => (
                            <dataGridFields.TimeField
                              {...props}
                              inputType="minutes"
                              outputType="short"
                            />
                          )}
                        />
                      );
                    }}
                  />
                  <HosInfoField
                    headerClassName="centered-content"
                    className="centered-content"
                    label="resources.drivers.labels.untilBreak"
                    field={(x) => x.break}
                    sortable
                    source="mustHaveBreakBy"
                  />
                  <HosInfoField
                    headerClassName="centered-content"
                    className="centered-content"
                    label="resources.drivers.labels.toDrive"
                    field={(x) => x.shiftDrive}
                    sortable
                    source="mustEndShiftDrivingBy"
                  />
                  <HosInfoField
                    headerClassName="centered-content"
                    className="centered-content"
                    label="resources.drivers.labels.shiftTimeLeft"
                    field={(x) => x.shiftWork}
                    sortable
                    source="mustEndShiftBy"
                  />
                  <HosInfoField
                    headerClassName="centered-content"
                    className="centered-content"
                    label="resources.drivers.labels.cycleTimeLeft"
                    field={(x) => x.cycleWork}
                    sortable
                    source="mustEndCycleBy"
                  />
                  <FunctionField<any>
                    source="lastPositionName"
                    render={renderLastPositionName}
                    label="resources.drivers.labels.lastPosition"
                  />
                  <dataGridFields.ConditionalField
                    conditionOperator="equals"
                    conditionValue={true}
                    source="eldConnected"
                    label="resources.drivers.labels.eldConnection"
                  >
                    <ForTrue>
                      <DriverEldConnectedField />
                    </ForTrue>
                    <ForFalse>
                      <DriverEldDisconnectedField />
                    </ForFalse>
                  </dataGridFields.ConditionalField>
                  <dataGridFields.ConditionalField
                    conditionOperator="equals"
                    conditionValue={true}
                    source="violations"
                    label="resources.drivers.labels.violations"
                  >
                    <ForTrue>
                      <DriverWithViolationsField history={props.history} />
                    </ForTrue>
                    <ForFalse>
                      <DriverNoViolationsField />
                    </ForFalse>
                  </dataGridFields.ConditionalField>
                </Datagrid>
              }
            />
          </DefaultProLogsList>
        </HosInfoSetterContext.Provider>
      </HosInfoContext.Provider>
    </div>
  );
};
